import React, { useState, useMemo } from 'react';
import { contactApi } from '../../services/api';
import { cartService } from '../../services/cartService';
import SuccessModal from '../common/SuccessModal';
import './BulkOrderForm.css';

interface BulkOrderFormData {
    name: string;
    phone: string;
    email?: string;
    location?: string;
    quantity?: string;
    requirements?: string;
    message?: string;
}

const BulkOrderForm: React.FC = () => {
    const [formData, setFormData] = useState<BulkOrderFormData>({
        name: '',
        phone: '',
        email: '',
        location: '',
        quantity: '',
        requirements: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cartItemIds = useMemo(() => {
        const items = cartService.getCartItems();
        return Array.from(new Set(items.map((item) => item.productId)));
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.phone.trim()) {
            setError('Name and phone number are required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const items = cartService.getCartItems();
            const ids = Array.from(new Set(items.map((item) => item.productId)));
            const cartIdsLine = ids.length > 0 ? `Items in cart: ${ids.join(', ')}` : '';

            const bulkOrderMessage = `BULK ORDER ENQUIRY\n\n` +
                `Location: ${formData.location || 'Not specified'}\n` +
                `Quantity: ${formData.quantity || 'Not specified'}\n` +
                `Requirements: ${formData.requirements || 'Not specified'}\n` +
                (cartIdsLine ? `${cartIdsLine}\n` : '') +
                `Additional Details: ${formData.message || 'None'}`;

            await contactApi.submit({
                name: formData.name,
                phone: formData.phone,
                email: formData.email || '',
                message: bulkOrderMessage
            });

            setShowSuccessModal(true);
            setFormData({
                name: '',
                phone: '',
                email: '',
                location: '',
                quantity: '',
                requirements: '',
                message: ''
            });
        } catch (err) {
            setError('Failed to submit form. Please try again.');
            console.error('Error submitting bulk order form:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bulk-order-form-container">
            <div className="bulk-order-form">
                <h3>Bulk Order Enquiry</h3>
                <p>Interested in bulk orders? Fill out the form below and we'll get back to you with special pricing and availability.</p>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="name">Name *</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Your full name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="Your phone number"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email (Optional)</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Your email address"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="location">Location (Optional)</label>
                        <input
                            type="text"
                            id="location"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="Your city/state or delivery location"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="quantity">Estimated Quantity (Optional)</label>
                        <input
                            type="text"
                            id="quantity"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleChange}
                            placeholder="e.g., 100 pieces, 50 units, etc."
                        />
                    </div>

                    {cartItemIds.length > 0 && (
                        <div className="form-group form-group--cart-ids">
                            <label>Items in your cart (will be included in enquiry)</label>
                            <p className="cart-ids-list">{cartItemIds.join(', ')}</p>
                        </div>
                    )}
                    <div className="form-group">
                        <label htmlFor="requirements">Product Requirements (Optional)</label>
                        <textarea
                            id="requirements"
                            name="requirements"
                            value={formData.requirements}
                            onChange={handleChange}
                            placeholder="Specify products, sizes, colors, or any special requirements"
                            rows={3}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="message">Additional Details (Optional)</label>
                        <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Any other information about your bulk order requirement"
                            rows={4}
                        />
                    </div>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary submit-btn"
                        disabled={loading}
                    >
                        {loading ? 'Submitting...' : 'Submit Bulk Order Enquiry'}
                    </button>
                </form>
            </div>
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                type="enquiry"
            />
        </div>
    );
};

export default BulkOrderForm;

